/**
 * Choose the words that belong on a figure, then draw them into its SVG.
 *
 * The AI platform accepts text only. It returns indexes into the runs we send.
 * It does not return a box. English and Welsh pages are asked separately, so
 * each language keeps its own words.
 *
 * @format
 */

import {
	ASSET_NEAR_MARGIN,
	CAPTION_WORD_LIST,
	isExactAssetCaption,
	type ClusteredFigure,
	type FigureBox,
} from "./extract-figure-svg.ts";

/** Knobs for the words offered to the model. Omit them and the code defaults apply. */
export interface WordingTune {
	nearMargin: number;
	labelBand: number;
	/** How far the label band reaches to the right of the figure, in points. */
	labelBandRight: number;
	/** How far the label band reaches below the figure, in points. */
	labelBandBelow: number;
	shortLabelWords: number;
	wordGap: number;
	lineTolerance: number;
	maxCandidates: number;
	/** When set, these words replace the built-in caption list. */
	captionWords?: readonly string[];
}

/** One text run near a figure, in PDF user space. The origin is the page bottom-left. */
export interface FigureTextRun {
	text: string;
	x: number;
	y: number;
	width: number;
	height: number;
}

const MAX_WORDING_CANDIDATES = 40;
/** A label of at most this many words can sit on the image. A question sentence is longer. */
const SHORT_LABEL_WORDS = 4;
/** A measured label (an axis title such as "Speed of moving air (m/s)") may run longer. */
const QUANTITY_LABEL_WORDS = 7;
/**
 * An axis title sits a line or two below (or beside) the plot box, further than
 * a plain label. A measured label may sit this far from the figure, in points.
 */
const QUANTITY_LABEL_BAND = 30;
/** One line of body text, in PDF points. A short label may sit this far from the image. */
const LABEL_BAND = 20;
/**
 * A series legend ("Intensive farming", "Free-range") sits to the right of a
 * plot, further out than a label above or below it. The right side of the label
 * band reaches this far, in points, so a right-of-plot legend is offered without
 * widening the other three sides onto unrelated body text.
 */
const LABEL_BAND_RIGHT = 42;
/**
 * A multi-line caption block ("9 cm³ hydrogen peroxide solution", "fresh potato
 * pieces") sits below a figure and runs several lines deep, further than a
 * single label line. The bottom of the label band reaches this far, in points,
 * so a caption block below a figure is offered without widening the other sides.
 */
const LABEL_BAND_BELOW = 70;
/**
 * A resume within this many points of the previous end is one split word.
 * The question paper reader uses this same gap. A wider gap is a new word.
 * A start further back is a different word on the same line.
 */
const WORD_GAP = 1.2;
/** A punctuation break (e.g. the "/" in "(m/s)") may gap this share of the text height. */
const PUNCT_JOIN_SHARE = 0.5;
/** Items within this vertical distance share a line. */
const LINE_TOLERANCE = 3;
const TEXT_HEIGHT_FALLBACK = 10;
const SVG_PAD = 2;
/** Share of the font size that sits below the baseline. The rest rises above it. */
const GLYPH_DESCENT = 0.2;

export const DEFAULT_WORDING_TUNE: WordingTune = {
	nearMargin: ASSET_NEAR_MARGIN,
	labelBand: LABEL_BAND,
	labelBandRight: LABEL_BAND_RIGHT,
	labelBandBelow: LABEL_BAND_BELOW,
	shortLabelWords: SHORT_LABEL_WORDS,
	wordGap: WORD_GAP,
	lineTolerance: LINE_TOLERANCE,
	maxCandidates: MAX_WORDING_CANDIDATES,
};

/** Instruction text sent before the candidate lines. `{language}` becomes English or Welsh. */
export const DEFAULT_WORDING_INSTRUCTIONS = [
	"This drawing is from the {language} paper.",
	"Each line is an index, a place, and the text. The place is on, above, below, left, or right.",
	"Choose the runs that are labels or captions of this figure.",
	"Leave out the question stem, the mark total, and words that belong to another figure.",
	"Do not translate a run. Do not invent a run. Return only indexes from the list.",
	"Return an empty list when none of the runs belong to the figure.",
].join("\n");

/** One candidate line. `{text}` keeps the JSON quotes. The import uses this line. */
export const DEFAULT_WORDING_LINE = "{index} [{place}]: {text}";

const LINE_TOKEN = /\{(index|place|text|raw|x|y|width|height)\}/g;

export const WORDING_RESPONSE_SCHEMA = {
	type: "OBJECT",
	properties: {
		include: { type: "ARRAY", items: { type: "NUMBER" } },
	},
	required: ["include"],
};

const formatNumber = (value: number): string => {
	const rounded = Math.round(value * 100) / 100;
	if (Object.is(rounded, -0) || Object.is(rounded, 0)) {
		return "0";
	}
	return String(rounded);
};

const escapeText = (value: string): string => value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const textHeight = (run: FigureTextRun): number => (run.height > 0 ? run.height : TEXT_HEIGHT_FALLBACK);

const runBox = (run: FigureTextRun): FigureBox => {
	const height = textHeight(run);
	return { x: run.x, y: run.y - height, width: Math.max(run.width, 1), height };
};

/**
 * Page text origin is the baseline. The substitute font rises by its font size.
 * This box is for the crop only. The shape check still uses runBox.
 */
const paintedBox = (run: FigureTextRun): FigureBox => {
	const height = textHeight(run);
	const descent = height * GLYPH_DESCENT;
	return {
		x: run.x,
		y: run.y - descent,
		width: Math.max(run.width, 1),
		height: height + descent,
	};
};

const intersects = (left: FigureBox, right: FigureBox): boolean =>
	left.x < right.x + right.width &&
	right.x < left.x + left.width &&
	left.y < right.y + right.height &&
	right.y < left.y + left.height;

const nearBoxOf = (figure: FigureBox, band: number): FigureBox => ({
	x: figure.x - band,
	y: figure.y - band,
	width: figure.width + band * 2,
	height: figure.height + band * 2,
});

/**
 * The label band, grown further on the right by {@link bandRight} and below by
 * {@link bandBelow}. A series legend sits to the right of a plot and a caption
 * block sits below it, each further out than a single label line, while the top
 * and left stay tight. The y axis points up, so "below" lowers the box origin.
 */
const labelBandBoxOf = (figure: FigureBox, band: number, bandRight: number, bandBelow: number): FigureBox => {
	const below = Math.max(band, bandBelow);
	return {
		x: figure.x - band,
		y: figure.y - below,
		width: figure.width + band + Math.max(band, bandRight),
		height: figure.height + below + band,
	};
};

const wordCount = (text: string): number => text.trim().split(/\s+/).filter(Boolean).length;

/**
 * Page and question furniture that is never a figure label: a mark allocation
 * such as "[1]", a question or part number alone such as "5." or "(a)" or
 * "(iv)", the paper code, the copyright line, and the "Examiner only" marking
 * header. The deterministic filter offers a wide candidate set and once relied
 * on the model to drop these. This predicate drops them so a model-free run
 * does not draw them onto a figure. Axis ticks (a bare number such as "40") are
 * not furniture and are left alone.
 */
const FURNITURE_PATTERNS: readonly RegExp[] = [
	/^\[\d+\]$/, // mark allocation, e.g. "[1]"
	/^\d+\.$/, // question number alone, e.g. "5."
	/^\(?[a-z]\)$/i, // part label alone, e.g. "(a)" or "a)"
	/^\(?[ivxlcdm]+\)$/i, // roman part label alone, e.g. "(i)", "(iv)"
	/^\([0-9A-Z]+-\d+\)$/i, // paper code, e.g. "(3400UA0-1)"
	/^©/, // copyright mark
	/wjec\s*cbac/i, // the awarding-body footer
	/^examiner$/i, // marking-column header, word one
	/^only$/i, // marking-column header, word two
];

const isFurniture = (text: string, pageNumber?: number): boolean => {
	const trimmed = text.trim();
	if (FURNITURE_PATTERNS.some((pattern) => pattern.test(trimmed))) {
		return true;
	}
	// A bare integer equal to the page number is the running page number, not an
	// axis tick. An axis tick rarely equals the page number, and a tick that does
	// is one lost label against many furniture numbers dropped.
	if (pageNumber !== undefined && /^\d+$/.test(trimmed) && Number(trimmed) === pageNumber) {
		return true;
	}
	return false;
};

/**
 * An axis title carries a unit in parentheses, such as "(m/s)", "(s)", "(a.u.)".
 * A plain question sentence does not, so the longer word limit stays off prose.
 */
const looksLikeQuantity = (text: string): boolean => /\([^)]*\)\s*$/.test(text.trim());

/** The most words a caption-led title line may run to. */
const CAPTION_LINE_WORDS = 8;
/** Words that lead a figure title line, built from the caption word list. */
const captionLeadPattern = (words: readonly string[]): RegExp => {
	const body = [...words]
		.map((word) => word.trim())
		.filter((word) => word.length > 0)
		.sort((left, right) => right.length - left.length)
		.map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
		.join("|");
	return new RegExp(`^(?:${body})\\s+\\d+(?:\\.\\d+)?\\b`, "i");
};
const DEFAULT_CAPTION_LEAD = captionLeadPattern(CAPTION_WORD_LIST);

/**
 * A figure title line that leads with a caption, such as "Graph 4.1 mean cilia
 * length in" or "Image 2.1 the apparatus". It names the figure then describes
 * it, so it runs longer than a plain label and belongs to the figure. A line
 * that only mentions a caption mid-sentence is not caption-led.
 */
const isCaptionLed = (text: string, captionWords?: readonly string[]): boolean => {
	const pattern = captionWords ? captionLeadPattern(captionWords) : DEFAULT_CAPTION_LEAD;
	return pattern.test(text.trim());
};

/** True when the join sits at a punctuation boundary, as in "(m" + "/" + "s)". */
const endsOrStartsPunct = (left: string, right: string): boolean => {
	const last = left.trim().slice(-1);
	const first = right.trim().slice(0, 1);
	const punct = /[/()\-.,+×x·]/;
	return punct.test(last) || punct.test(first);
};

const inMargin = (figure: FigureBox, run: FigureTextRun, nearMargin: number): boolean =>
	intersects(nearBoxOf(figure, nearMargin), runBox(run));

const inLabelBand = (
	figure: FigureBox,
	run: FigureTextRun,
	labelBand: number,
	labelBandRight: number,
	labelBandBelow: number,
): boolean => intersects(labelBandBoxOf(figure, labelBand, labelBandRight, labelBandBelow), runBox(run));

/**
 * Join items on one line when the next item resumes within WORD_GAP.
 * The joined text is one word. A piece of that word is never offered alone.
 */
const joinSplitWords = (items: readonly FigureTextRun[], tune: WordingTune): FigureTextRun[] => {
	const rows = new Map<number, FigureTextRun[]>();
	for (const item of items) {
		if (item.text.trim() === "") {
			continue;
		}
		const key = Math.round(item.y / tune.lineTolerance);
		const row = rows.get(key);
		if (row) {
			row.push(item);
		} else {
			rows.set(key, [item]);
		}
	}
	const runs: FigureTextRun[] = [];
	for (const row of rows.values()) {
		const parts = [...row].sort((left, right) => left.x - right.x || left.text.localeCompare(right.text));
		const line: FigureTextRun[] = [];
		for (const part of parts) {
			const previous = line[line.length - 1];
			const gap = previous ? part.x - (previous.x + previous.width) : tune.wordGap;
			// A split word resumes within wordGap. A punctuation break such as the
			// "/" in "(m/s)" leaves a slightly wider gap, so also join a short gap
			// at a punctuation boundary, scaled to the text height.
			const isAtPunct = previous ? endsOrStartsPunct(previous.text, part.text) : false;
			const fontGap = previous && isAtPunct ? Math.max(previous.height, part.height) * PUNCT_JOIN_SHARE : 0;
			const joinLimit = Math.max(tune.wordGap, fontGap);
			if (previous && gap < joinLimit && gap > -joinLimit) {
				const end = Math.max(previous.x + previous.width, part.x + part.width);
				previous.text += part.text;
				previous.width = end - previous.x;
				previous.height = Math.max(previous.height, part.height);
			} else {
				line.push({ ...part });
			}
		}
		runs.push(...line);
	}
	return runs;
};

/**
 * An exact caption may sit in the 32 point margin.
 * Any other run must be a short label on the image or within one line.
 * A long line is dropped even when it is narrower than the image.
 */
const offerRun = (figure: FigureBox, run: FigureTextRun, tune: WordingTune, pageNumber?: number): boolean => {
	const text = run.text.trim();
	if (text.length === 0) {
		return false;
	}
	// Page and question furniture is never a figure label, wherever it sits.
	if (isFurniture(text, pageNumber)) {
		return false;
	}
	if (isExactAssetCaption(text, tune.captionWords)) {
		return inMargin(figure, run, tune.nearMargin);
	}
	if (isCaptionLed(text, tune.captionWords)) {
		// A caption-led title line names the figure then describes it. It runs
		// longer than a plain label and sits a line or two from the figure.
		return (
			wordCount(text) <= CAPTION_LINE_WORDS &&
			inLabelBand(
				figure,
				run,
				QUANTITY_LABEL_BAND,
				Math.max(QUANTITY_LABEL_BAND, tune.labelBandRight),
				Math.max(QUANTITY_LABEL_BAND, tune.labelBandBelow),
			)
		);
	}
	if (looksLikeQuantity(text)) {
		// An axis title sits further from the plot and may run longer.
		return (
			wordCount(text) <= QUANTITY_LABEL_WORDS &&
			inLabelBand(
				figure,
				run,
				QUANTITY_LABEL_BAND,
				Math.max(QUANTITY_LABEL_BAND, tune.labelBandRight),
				Math.max(QUANTITY_LABEL_BAND, tune.labelBandBelow),
			)
		);
	}
	return (
		wordCount(text) <= tune.shortLabelWords &&
		inLabelBand(figure, run, tune.labelBand, tune.labelBandRight, tune.labelBandBelow)
	);
};

/** Where the run sits relative to the figure. A run on the figure is "on". */
const placeOf = (figure: FigureBox, run: FigureTextRun): "on" | "above" | "below" | "left" | "right" => {
	const box = runBox(run);
	if (intersects(figure, box)) {
		return "on";
	}
	const centerX = box.x + box.width / 2;
	const centerY = box.y + box.height / 2;
	const gapX =
		centerX < figure.x
			? figure.x - centerX
			: centerX > figure.x + figure.width
				? centerX - (figure.x + figure.width)
				: 0;
	const gapY =
		centerY < figure.y
			? figure.y - centerY
			: centerY > figure.y + figure.height
				? centerY - (figure.y + figure.height)
				: 0;
	if (gapY >= gapX) {
		return centerY > figure.y ? "above" : "below";
	}
	return centerX < figure.x ? "left" : "right";
};

const distanceToFigure = (figure: FigureBox, run: FigureTextRun): number => {
	const box = runBox(run);
	const centerX = box.x + box.width / 2;
	const centerY = box.y + box.height / 2;
	const nearestX = Math.max(figure.x, Math.min(centerX, figure.x + figure.width));
	const nearestY = Math.max(figure.y, Math.min(centerY, figure.y + figure.height));
	const dx = centerX - nearestX;
	const dy = centerY - nearestY;
	return dx * dx + dy * dy;
};

/** Items on the same baseline sit within this many points. */
const CAPTION_LINE_TOL = 3;
/** A run to the left closer than this does not count as separate body text. */
const CAPTION_LEFT_CLEAR = 6;

/**
 * A caption run that is a reference inside a sentence, not a caption that owns a
 * figure. "The results are shown in Graph 2.2 and Tables 2.3" names Tables 2.3
 * mid-line, so "Tables 2.3" has body text to its left. A standalone caption
 * that leads its line has nothing to its left. A reference belongs to another
 * figure, so it is not offered as a label for this one.
 */
const isInlineCaptionReference = (
	run: FigureTextRun,
	items: readonly FigureTextRun[],
	captionWords?: readonly string[],
): boolean => {
	// A trailing period on an inline mention ("... shown in Image 3.2.") must not
	// hide the caption shape, so test the run without it.
	const withoutTrailingDot = run.text.trim().replace(/\.$/, "");
	if (!isExactAssetCaption(withoutTrailingDot, captionWords)) {
		return false;
	}
	return items.some(
		(other) =>
			other !== run &&
			Math.abs(other.y - run.y) <= CAPTION_LINE_TOL &&
			other.x < run.x &&
			other.x + other.width <= run.x + CAPTION_LEFT_CLEAR,
	);
};

/** A following run this close to the right continues the same sentence. */
const SENTENCE_CONTINUE_GAP = 12;

/**
 * A run that starts or sits inside a sentence that flows on to its right, not a
 * label. A stem fragment such as "temperature of 35" is immediately followed on
 * the same line by more sentence text ("ºC. The apparatus is shown in ..."). A
 * label such as "protease" has nothing close to its right. The test is a run on
 * the same baseline that begins within a small gap of this run's right edge. An
 * exact caption is exempt — a caption that leads a description line is judged
 * elsewhere.
 */
const isOnSentenceLine = (
	run: FigureTextRun,
	items: readonly FigureTextRun[],
	captionWords?: readonly string[],
): boolean => {
	if (isExactAssetCaption(run.text.trim().replace(/\.$/, ""), captionWords)) {
		return false;
	}
	const runRight = run.x + run.width;
	return items.some(
		(other) =>
			other !== run &&
			Math.abs(other.y - run.y) <= CAPTION_LINE_TOL &&
			other.x >= runRight - CAPTION_LEFT_CLEAR &&
			other.x <= runRight + SENTENCE_CONTINUE_GAP,
	);
};

/** A prose line directly above sits within this many points higher. */
const PARAGRAPH_LINE_GAP = 18;
/** A line counts as above when its left edge is within this many points. */
const PARAGRAPH_LEFT_ALIGN = 20;
/** A line of this many words or more is body prose, not a label. */
const PROSE_LINE_WORDS = 8;
/**
 * The runs on one baseline total at most this many words on a label row. A
 * figure's label row is a few short labels across columns; a wrapped question
 * or prose line totals far more. A run sharing a baseline whose words sum above
 * this is part of a prose line, not a label.
 */
const PROSE_SAME_LINE_WORDS = 12;

/**
 * A run that is the last line of a body paragraph, not a label. A paragraph tail
 * such as "tubing was tested again." or "shoot." sits directly below a full
 * prose line at the same left margin, so it is the continuation of that
 * paragraph. A caption-block label ("fresh potato pieces") has only other short
 * labels above it, never a prose line. The test is a line of five words or more
 * directly above the run at the same left edge. An exact caption is exempt.
 */
const isParagraphTail = (
	run: FigureTextRun,
	items: readonly FigureTextRun[],
	captionWords?: readonly string[],
): boolean => {
	if (isExactAssetCaption(run.text.trim().replace(/\.$/, ""), captionWords)) {
		return false;
	}
	return items.some(
		(other) =>
			other !== run &&
			other.y > run.y + CAPTION_LINE_TOL &&
			other.y < run.y + PARAGRAPH_LINE_GAP &&
			Math.abs(other.x - run.x) < PARAGRAPH_LEFT_ALIGN &&
			wordCount(other.text) >= PROSE_LINE_WORDS,
	);
};

/**
 * A run that sits on a baseline whose runs total a prose line's worth of words,
 * not a label row. A question line ("I. Describe the effect of increasing...")
 * or a wrapped sentence ending in a stray unit ("... ate only 1100 g") totals
 * many words on its baseline, while a figure's label row is a few short labels
 * across columns. An exact caption is exempt.
 */
const isOnProseLine = (
	run: FigureTextRun,
	items: readonly FigureTextRun[],
	captionWords?: readonly string[],
): boolean => {
	const text = run.text.trim();
	if (isExactAssetCaption(text.replace(/\.$/, ""), captionWords) || isCaptionLed(text, captionWords)) {
		return false;
	}
	const lineWords = items
		.filter((other) => Math.abs(other.y - run.y) <= CAPTION_LINE_TOL)
		.reduce((total, other) => total + wordCount(other.text), 0);
	return lineWords > PROSE_SAME_LINE_WORDS;
};

/** A question part marker that opens a new part: "(a)", "(i)", "5.". */
const PART_MARKER = /^\(?[a-z]\)$|^\(?[ivxlcdm]+\)$|^\d+\.$/i;

/**
 * A run below the figure that a question part marker separates from it. A marker
 * such as "(a)" or "(i)" opens the next question part, so a label below that
 * marker belongs to the next question, not the figure above it. A figure's own
 * caption block below it has no part marker between it and the figure.
 */
const isBelowQuestionBoundary = (run: FigureTextRun, figure: FigureBox, items: readonly FigureTextRun[]): boolean => {
	if (run.y >= figure.y - CAPTION_LINE_TOL) {
		return false;
	}
	return items.some(
		(other) => other.y > run.y + CAPTION_LINE_TOL && other.y < figure.y && PART_MARKER.test(other.text.trim()),
	);
};

/** Whole words that pass the shape check. The closest 24 are kept. A repeated caption is dropped. */
export const wordingCandidates = (
	figure: FigureBox,
	items: readonly FigureTextRun[],
	tune: WordingTune = DEFAULT_WORDING_TUNE,
	pageNumber?: number,
): FigureTextRun[] => {
	const joined = joinSplitWords(items, tune);
	const offered = joined
		.filter((item) => offerRun(figure, item, tune, pageNumber))
		// A caption that is a mid-line reference names another figure. Drop it.
		.filter((item) => !isInlineCaptionReference(item, joined, tune.captionWords))
		// A short run that starts a sentence line is stem text, not a label.
		.filter((item) => !isOnSentenceLine(item, joined, tune.captionWords))
		// A short run below a prose line is a paragraph tail, not a label.
		.filter((item) => !isParagraphTail(item, joined, tune.captionWords))
		// A short run on a line that totals a sentence is prose, not a label.
		.filter((item) => !isOnProseLine(item, joined, tune.captionWords))
		// A run below a question part marker belongs to the next question.
		.filter((item) => !isBelowQuestionBoundary(item, figure, joined))
		.sort(
			(left, right) =>
				distanceToFigure(figure, left) - distanceToFigure(figure, right) ||
				right.y - left.y ||
				left.x - right.x ||
				left.text.localeCompare(right.text),
		);
	const seenCaptions = new Set<string>();
	const unique: FigureTextRun[] = [];
	for (const item of offered) {
		const text = item.text.trim();
		if (isExactAssetCaption(text, tune.captionWords)) {
			const key = text.toLowerCase();
			if (seenCaptions.has(key)) {
				continue;
			}
			seenCaptions.add(key);
		}
		unique.push(item);
	}
	return unique.slice(0, tune.maxCandidates);
};

/** Prompt for one figure. The model may return only indexes from this list. */
export const buildWordingPrompt = (input: {
	language: "en" | "cy";
	figure: FigureBox;
	candidates: readonly FigureTextRun[];
	/** Instruction text. `{language}` becomes English or Welsh. Omit it to use the code default. */
	instructions?: string;
	/** Candidate line. Omit it to use `{index} [{place}]: {text}`. */
	lineFormat?: string;
}): string => {
	const languageName = input.language === "cy" ? "Welsh" : "English";
	const instructions = (input.instructions ?? DEFAULT_WORDING_INSTRUCTIONS).split("{language}").join(languageName);
	const format = input.lineFormat ?? DEFAULT_WORDING_LINE;
	const lines = input.candidates
		.map((run, index) =>
			format.replace(LINE_TOKEN, (_token, key: string) => {
				switch (key) {
					case "index":
						return String(index);
					case "place":
						return placeOf(input.figure, run);
					case "text":
						return JSON.stringify(run.text);
					case "raw":
						return run.text;
					case "x":
						return formatNumber(run.x);
					case "y":
						return formatNumber(run.y);
					case "width":
						return formatNumber(run.width);
					case "height":
						return formatNumber(run.height);
					default:
						return "";
				}
			}),
		)
		.join("\n");
	return [instructions, "", lines].join("\n");
};

const parsedBody = (raw: unknown): unknown => {
	if (typeof raw !== "string") {
		return raw;
	}
	try {
		return JSON.parse(raw);
	} catch {
		return undefined;
	}
};

/** Indexes the model chose. An index outside the candidate list is dropped. */
export const wordingIndexes = (raw: unknown, count: number): number[] => {
	const body = parsedBody(raw);
	if (!body || typeof body !== "object") {
		return [];
	}
	const include = (body as { include?: unknown }).include;
	if (!Array.isArray(include)) {
		return [];
	}
	const seen = new Set<number>();
	const indexes: number[] = [];
	for (const item of include) {
		const index = typeof item === "number" ? item : typeof item === "string" ? Number(item) : Number.NaN;
		if (!Number.isInteger(index) || index < 0 || index >= count || seen.has(index)) {
			continue;
		}
		seen.add(index);
		indexes.push(index);
	}
	return indexes;
};

const svgInner = (svg: string): string | undefined => {
	const match = /^<svg\b[^>]*>([\s\S]*)<\/svg>$/.exec(svg.trim());
	return match ? match[1] : undefined;
};

const unionBox = (left: FigureBox, right: FigureBox): FigureBox => {
	const x = Math.min(left.x, right.x);
	const y = Math.min(left.y, right.y);
	const maxX = Math.max(left.x + left.width, right.x + right.width);
	const maxY = Math.max(left.y + left.height, right.y + right.height);
	return { x, y, width: maxX - x, height: maxY - y };
};

/**
 * Draw the chosen runs into the figure SVG and grow the box to cover them.
 * An empty list returns the same figure. The substitute font is sans-serif.
 */
export const expandFigureWithWording = (
	figure: ClusteredFigure,
	runs: readonly FigureTextRun[],
	tune: WordingTune = DEFAULT_WORDING_TUNE,
): ClusteredFigure => {
	const chosen = joinSplitWords(runs, tune).filter((run) => offerRun(figure, run, tune));
	if (chosen.length === 0) {
		return figure;
	}
	const inner = svgInner(figure.svg);
	if (inner === undefined) {
		return figure;
	}
	let box: FigureBox = { x: figure.x, y: figure.y, width: figure.width, height: figure.height };
	for (const run of chosen) {
		box = unionBox(box, paintedBox(run));
	}
	box = {
		x: box.x - SVG_PAD,
		y: box.y - SVG_PAD,
		width: box.width + SVG_PAD * 2,
		height: box.height + SVG_PAD * 2,
	};
	const dx = figure.x - box.x;
	const dy = box.y + box.height - (figure.y + figure.height);
	const texts = chosen
		.map((run) => {
			const height = textHeight(run);
			const x = run.x - box.x;
			const y = box.y + box.height - run.y;
			const width = Math.max(run.width, 1);
			return `<text x="${formatNumber(x)}" y="${formatNumber(y)}" font-size="${formatNumber(height)}" font-family="sans-serif" textLength="${formatNumber(width)}" lengthAdjust="spacingAndGlyphs">${escapeText(run.text)}</text>`;
		})
		.join("");
	const width = formatNumber(box.width);
	const height = formatNumber(box.height);
	const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}"><g transform="translate(${formatNumber(dx)} ${formatNumber(dy)})">${inner}</g>${texts}</svg>`;
	return { ...figure, x: box.x, y: box.y, width: box.width, height: box.height, svg };
};
